import type { CmmdTaskConversation, CmmdTaskConversationSummary } from "@cmmd-forge/contracts";

export interface TaskConversationHistoryState {
  readonly taskId: number | null;
  readonly conversations: readonly CmmdTaskConversationSummary[];
  readonly conversationsNextCursor: string | null;
  readonly selectedConversation: CmmdTaskConversation | null;
  readonly expandedConversationId: string | null;
  readonly loaded: boolean;
  readonly loading: boolean;
  readonly loadingMoreConversations: boolean;
  readonly loadingConversationId: string | null;
  readonly loadingMoreEvents: boolean;
  readonly error: string | null;
  readonly detailError: string | null;
}

export type TaskConversationHistoryAction =
  | { readonly type: "reset"; readonly taskId: number | null }
  | { readonly type: "list-start"; readonly append: boolean }
  | {
      readonly type: "list-ok";
      readonly append: boolean;
      readonly conversations: readonly CmmdTaskConversationSummary[];
      readonly nextCursor: string | null;
    }
  | { readonly type: "list-error"; readonly message: string }
  | { readonly type: "detail-start"; readonly conversationId: string; readonly append: boolean }
  | {
      readonly type: "detail-ok";
      readonly append: boolean;
      readonly conversation: CmmdTaskConversation;
    }
  | { readonly type: "detail-error"; readonly message: string }
  | { readonly type: "detail-clear" };

type ListAction = Extract<TaskConversationHistoryAction, { readonly type: `list-${string}` }>;
type DetailAction = Exclude<TaskConversationHistoryAction, ListAction | { readonly type: "reset" }>;

export function initialTaskConversationHistoryState(
  taskId: number | null,
): TaskConversationHistoryState {
  return {
    taskId,
    conversations: [],
    conversationsNextCursor: null,
    selectedConversation: null,
    expandedConversationId: null,
    loaded: false,
    loading: false,
    loadingMoreConversations: false,
    loadingConversationId: null,
    loadingMoreEvents: false,
    error: null,
    detailError: null,
  };
}

export function taskConversationHistoryReducer(
  state: TaskConversationHistoryState,
  action: TaskConversationHistoryAction,
): TaskConversationHistoryState {
  if (action.type === "reset") return initialTaskConversationHistoryState(action.taskId);
  if (isListAction(action)) return reduceListAction(state, action);
  return reduceDetailAction(state, action);
}

function isListAction(action: TaskConversationHistoryAction): action is ListAction {
  return action.type.startsWith("list-");
}

function reduceListAction(
  state: TaskConversationHistoryState,
  action: ListAction,
): TaskConversationHistoryState {
  switch (action.type) {
    case "list-start":
      return {
        ...state,
        loading: !action.append,
        loadingMoreConversations: action.append,
        error: null,
      };
    case "list-ok":
      return {
        ...state,
        conversations: action.append
          ? mergeByConversationId(state.conversations, action.conversations)
          : action.conversations,
        conversationsNextCursor: action.nextCursor,
        loaded: true,
        loading: false,
        loadingMoreConversations: false,
        error: null,
      };
    case "list-error":
      return {
        ...state,
        loaded: true,
        loading: false,
        loadingMoreConversations: false,
        error: action.message,
      };
  }
}

function reduceDetailAction(
  state: TaskConversationHistoryState,
  action: DetailAction,
): TaskConversationHistoryState {
  switch (action.type) {
    case "detail-start":
      return {
        ...state,
        selectedConversation: action.append ? state.selectedConversation : null,
        expandedConversationId: action.conversationId,
        loadingConversationId: action.conversationId,
        loadingMoreEvents: action.append,
        detailError: null,
      };
    case "detail-ok":
      return {
        ...state,
        selectedConversation:
          action.append && state.selectedConversation !== null
            ? {
                ...action.conversation,
                events: prependByEventId(
                  state.selectedConversation.events,
                  action.conversation.events,
                ),
              }
            : action.conversation,
        loadingConversationId: null,
        loadingMoreEvents: false,
        detailError: null,
      };
    case "detail-error":
      return {
        ...state,
        loadingConversationId: null,
        loadingMoreEvents: false,
        detailError: action.message,
      };
    case "detail-clear":
      return {
        ...state,
        selectedConversation: null,
        expandedConversationId: null,
        loadingConversationId: null,
        loadingMoreEvents: false,
        detailError: null,
      };
  }
}

function mergeByConversationId(
  current: readonly CmmdTaskConversationSummary[],
  next: readonly CmmdTaskConversationSummary[],
): readonly CmmdTaskConversationSummary[] {
  const seen = new Set(current.map((conversation) => conversation.conversationId));
  return [...current, ...next.filter((conversation) => !seen.has(conversation.conversationId))];
}

function prependByEventId(
  current: CmmdTaskConversation["events"],
  next: CmmdTaskConversation["events"],
): CmmdTaskConversation["events"] {
  const seen = new Set(current.map((event) => event.eventId));
  return [...next.filter((event) => !seen.has(event.eventId)), ...current];
}
